import { For, createSignal, Show } from "solid-js"
import { useSearchParams } from "@solidjs/router"
import { createSkillMarketController } from "../skill-market/skill-market-controller"
import { useSkillMarketDirectory } from "../skill-market/skill-market-directory"
import { SkillMarketView } from "../skill-market/skill-market-view"
import { createExpertController, ExpertView } from "../expert"
import { useServer } from "@/context/server"
import { ReservedPlaceholder } from "../reserved"
import expertIconRaw from "@/assets/self-develop/expert.svg?raw"
import skillIconRaw from "@/assets/self-develop/skill.svg?raw"
import connectorIconRaw from "@/assets/self-develop/connector.svg?raw"
import searchIconRaw from "@/assets/self-develop/seach.svg?raw"
import createIconRaw from "@/assets/self-develop/create.svg?raw"
import "../skill-market/skill-market.css"
import "../expert/expert.css"
import "./expertise-tabs.css"

type ExpertiseTab = "experts" | "skills" | "connectors"

// svg 里用 <style>.a{fill:...}</style> + class="a" 上色，innerHTML 注入后 <style>
// 变成全局作用域，多个图标共用类名 .a 会互相覆盖。这里剥掉 <style> 与 class/fill，
// 改在 <svg> 根元素按需设色，让路径继承（同 new-session-view 的 ExpertIcon）。
// 顺带给 <rect> 补 fill="none"：seach.svg 里那个透明占位 rect 的原色写在 .a{fill:none}，
// 剥掉 style 后会退回默认黑色实心块，把放大镜整个盖住。
function inlineIcon(src: string, color: string) {
  return src
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/\s(class|fill)\s*=\s*"[^"]*"/gi, "")
    .replace(/<rect\b/gi, '<rect fill="none"')
    .replace(/<svg\b/i, `<svg fill="${color}"`)
}

function TabIcon(props: { src: string; active: boolean }) {
  return (
    <span
      class="expertise-tab__icon"
      aria-hidden="true"
      innerHTML={inlineIcon(props.src, props.active ? "#1e293b" : "#767676")}
    />
  )
}

const TABS: { key: ExpertiseTab; label: string; icon: string }[] = [
  { key: "experts", label: "专家", icon: expertIconRaw },
  { key: "skills", label: "技能", icon: skillIconRaw },
  { key: "connectors", label: "连接器", icon: connectorIconRaw },
]

export function ExpertisePage() {
  const server = useServer()
  const getSkillMarketDirectory = useSkillMarketDirectory()
  const skillController = createSkillMarketController(() => server.current?.http, getSkillMarketDirectory)
  const expertController = createExpertController(() => server.current)
  const [params] = useSearchParams()
  const [tab, setTab] = createSignal<ExpertiseTab>(
    params.tab === "experts" || params.tab === "skills" || params.tab === "connectors"
      ? (params.tab as ExpertiseTab)
      : "experts",
  )

  return (
    <div class="scheduled-tasks-root">
      <div class="expertise-tabs">
        <For each={TABS}>
          {(item) => (
            <button
              class="expertise-tab"
              classList={{ "expertise-tab--active": tab() === item.key }}
              onClick={() => setTab(item.key)}
            >
              <TabIcon src={item.icon} active={tab() === item.key} />
              <span>{item.label}</span>
            </button>
          )}
        </For>

        {/* 专家页的搜索 / 创建，跟在分段标签右侧（技能、连接器暂不涉及） */}
        <Show when={tab() === "experts"}>
          <div class="expertise-actions">
            <label class="expertise-search">
              <span
                class="expertise-search__icon"
                aria-hidden="true"
                innerHTML={inlineIcon(searchIconRaw, "#a2a2a2")}
              />
              <input
                class="expertise-search__input"
                type="text"
                placeholder="搜索专家名称、标签..."
                value={expertController.searchQuery()}
                onInput={(event) => expertController.setSearchQuery(event.currentTarget.value)}
              />
            </label>
            <button class="expertise-create-btn" onClick={() => expertController.openCreate()}>
              <span
                class="expertise-create-btn__icon"
                aria-hidden="true"
                innerHTML={inlineIcon(createIconRaw, "#717171")}
              />
              <span>创建专家</span>
            </button>
          </div>
        </Show>
      </div>

      <Show when={tab() === "experts"}>
        <ExpertView controller={expertController} />
      </Show>
      <Show when={tab() === "skills"}>
        <SkillMarketView controller={skillController} />
      </Show>
      <Show when={tab() === "connectors"}>
        <ReservedPlaceholder title="连接器" />
      </Show>
    </div>
  )
}

export default ExpertisePage
